import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import servicesData from "../data/services.json";
import Enquiry from "../components/sections/Enquiry";
import { PiCertificate, PiStudent, PiChalkboardTeacher } from "react-icons/pi";
import { FiBookOpen } from "react-icons/fi";
import { GrUserExpert } from "react-icons/gr";
import { FaLaptopCode } from "react-icons/fa";

export default function CourseDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const course = servicesData.courses.find((c) => c.slug === slug);

  if (!course) return null;

  return (
    <div className="bg-white min-h-screen">
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 
                       font-medium transition-colors group"
          >
            <svg
              className="w-5 h-5 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Courses
          </button>
        </div>
      </div>

      <section className="bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="text-gray-500 text-sm mb-6 flex items-center gap-2 font-medium">
                <span className="hover:text-gray-700 cursor-pointer">
                  Courses
                </span>
                <span>/</span>
                <span className="text-gray-700">{course.title}</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-gray-900">
                {course.title}
              </h1>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {course.desc}
              </p>

              <div className="flex flex-wrap gap-8 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center">
                    <span className="text-xl">
                      <PiStudent />
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">
                      Enrolled Students
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {course.students}+
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center">
                    <span className="text-xl">⏱</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">
                      Duration
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {course.duration}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center">
                    <span className="text-xl">
                      <PiCertificate />
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">
                      Certification
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      Included
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => {
                    setShowEnquiry(true);

                    setTimeout(() => {
                      document
                        .getElementById("course-enquiry")
                        ?.scrollIntoView({
                          behavior: "smooth",
                        });
                    }, 200);
                  }}
                  className="bg-gray-900 text-white px-8 py-4 rounded-lg 
             font-semibold text-base hover:bg-gray-800 
             transition-all shadow-lg hover:shadow-xl"
                >
                  Enquire Now
                </button>

                <button
                  onClick={() => {
                    setPendingDownload(course.syllabus);
                    setShowEnquiry(true);

                    setTimeout(() => {
                      document
                        .getElementById("course-enquiry")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }, 200);
                  }}
                  className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg 
             font-semibold text-base hover:border-gray-400 hover:bg-gray-50 
             transition-all"
                >
                  Download Syllabus
                </button>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-100">
                  <img
                    src={course.img}
                    alt={course.title}
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-16">
            <section>
              <h2 className="text-3xl font-bold mb-8 text-gray-900">
                What you'll learn
              </h2>

              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
                  {course.learn.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <svg
                        className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <p className="text-gray-700 font-medium leading-relaxed">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-8 text-gray-900">
                About this course
              </h2>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">
                  {course.about}
                </p>
              </div>
            </section>
            <section>
              <h2 className="text-3xl font-bold mb-8 text-gray-900">
                Course Features
              </h2>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all">
                  <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-3xl">⏱</span>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">
                    {course.duration}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Comprehensive curriculum designed for deep learning
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all">
                  <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-3xl">
                      <FiBookOpen />
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">
                    Structured Learning
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Step-by-step guidance with practical projects
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all">
                  <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-3xl">
                      <PiCertificate />
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">
                    Certification
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Industry-recognized certificate upon completion
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all">
                  <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-3xl">
                      <GrUserExpert />
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">
                    Expert Instructors
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Learn from industry professionals with real experience
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all">
                  <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-3xl">
                      <PiChalkboardTeacher />
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">
                    Support & Mentorship
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Get guidance whenever you need help
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all">
                  <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-3xl">
                      <FaLaptopCode />
                    </span>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">
                    Real-World Projects
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Build portfolio-worthy projects while learning
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-8 text-gray-900">
                Certificate of Completion
              </h2>

              <div className="max-w-2xl mx-auto">
                <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-gray-50 p-4">
                  <img
                    src="/images/preview-certificate.png"
                    alt="Certificate Preview"
                    className="w-full h-auto rounded"
                  />
                </div>

                <p className="text-gray-500 text-center mt-4 text-sm">
                  Earn a professional certificate upon course completion
                </p>
              </div>
            </section>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-6 text-lg">
                  This course includes:
                </h3>

                <ul className="space-y-4">
                  <li className="flex items-start gap-3 text-gray-700">
                    <svg
                      className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      {course.duration} of comprehensive content
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700">
                    <svg
                      className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      Hands-on projects and assignments
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700">
                    <svg
                      className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      Certificate of completion
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700">
                    <svg
                      className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      Expert instructor support
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-700">
                    <svg
                      className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      Access on mobile and desktop
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-sm">
                <button
                  onClick={() => {
                    setShowEnquiry(true);

                    setTimeout(() => {
                      document
                        .getElementById("course-enquiry")
                        ?.scrollIntoView({
                          behavior: "smooth",
                        });
                    }, 200);
                  }}
                  className="w-full bg-gray-900 text-white py-4 rounded-lg 
             font-semibold text-base hover:bg-gray-800 
             transition-all shadow-md hover:shadow-lg mb-3"
                >
                  Enquire Now
                </button>

                <button
                  onClick={() => {
                    setPendingDownload(course.syllabus);
                    setShowEnquiry(true);

                    setTimeout(() => {
                      document
                        .getElementById("course-enquiry")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }, 200);
                  }}
                  className="w-full border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg 
             font-semibold text-base hover:border-gray-400 hover:bg-gray-50 
             transition-all"
                >
                  Download Syllabus
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showEnquiry && (
        <section id="course-enquiry" className="mt-24">
          <Enquiry
            enquiryType={course.title}
            downloadMode={!!pendingDownload}
            onSuccess={() => {
              if (pendingDownload) {
                const link = document.createElement("a");
                link.href = pendingDownload;
                link.download = "";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }

              setPendingDownload(null);
            }}
          />
        </section>
      )}
    </div>
  );
}
